(ns app.front.view.approval.form
  (:require [app.domain.application.condition.model :as condition-model]
            [app.domain.approval.model :as approval-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.util :as du]
            [app.domain.workspace.model :as workspace-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.multi-select :as ui-ms]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.ui.separator :as ui-sep]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.conditions.common :as condition-common]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def default-new-circulation-state
  {:target-group-id         nil
   :target-user-ids         []
   :due-date                nil
   :description             ""
   :visibility-stakeholders false
   :visibility-applicants   false
   :targets                 []})

;; Events
(rf/reg-event-db ::close-new-circulation-form
  #(assoc % ::new-circulation-form-open false))

(rf/reg-event-db ::save-new-circulation-data
  (rf/path [::new-circulation-data])
  (fn [nfdata [_ data]]
    (merge nfdata data)))

(rf/reg-event-db ::reset-new-circulation-data
  (rf/path [::new-circulation-data])
  (constantly nil))

;; Subs
(rf/reg-sub ::new-circulation-form-open :-> ::new-circulation-form-open)

(rf/reg-sub ::stakeholder-groups
  :<- [::ws-subs/authority-group]
  (fn [auth-group _]
    (->> auth-group
         :group/approver-groups
         (mapcat :entity/_parent)
         (filter #(-> % :group/roles (contains? :group.role/approver)))
         (sort-by :group/long-name)
         seq)))

(rf/reg-sub ::stakeholder-users
  :<- [::entities/context]
  :<- [:session/user]
  (fn [[ctx current-user] [_ group-id]]
    (when group-id
      (->> (entities/find-entities ctx
                                   (filter #(some (fn [g]
                                                    (= group-id g))
                                                  (:user/groups %))))
           (sort (partial user-model/user-sort (:db/id current-user)))))))

(rf/reg-sub ::stakeholder-user
  (fn [[_ group-id _]]
    (rf/subscribe [::stakeholder-users group-id]))
  (fn [all-users [_ _ user-id]]
    (->> all-users
         (filter (comp (partial = user-id) :db/id))
         first)))

(rf/reg-sub ::no-such-user?
  (fn [[_ group-id user-id]]
    (rf/subscribe [::stakeholder-user group-id user-id]))
  (fn [user [_ _ user-id]]
    ;; id specified, but user not found
    (and user-id (nil? user))))

(rf/reg-sub ::deviant-approval-ws-configs
  (fn [[_ approval-id]]
    [(rf/subscribe [::ws-subs/config-tuple])
     (rf/subscribe [::ws-subs/permit-type])
     (rf/subscribe [::entities/entity approval-id])])
  (fn [[ws-config-tuple permit-type approval] [_ _ attr]]
    (when-some [approval-configs (some->> approval attr set (workspace-model/leaf->config-path permit-type))]
      (when (not= approval-configs ws-config-tuple)
        approval-configs))))

(defn- process-data-for-create [data]
  (-> (cond-> (dissoc data :target-user-id :target-group-id)
        (-> data :description str/blank?) (dissoc :description)
        (-> data :due-date nil?)          (dissoc :due-date))
      (update :targets (fn [targets]
                         (map (fn [target]
                                (if-let [target-user-id (:target-user-id target)]
                                  (-> target
                                      (assoc :target-user-ids [target-user-id])
                                      (dissoc :target-user-id))
                                  (dissoc target :target-user-id)))
                              targets)))))

(defn- process-data-for-edit [user-invalid? data]
  (cond-> (dissoc data :description :target-group-id :conditions :saving?)
    (-> data :due-date nil?) (dissoc :due-date)
    (or (-> data :target-user-id number? not)
        user-invalid?) (dissoc :target-user-id)))

(rf/reg-sub ::selectable-conditions
  (fn [[_ process-target-id]]
    [(rf/subscribe [::condition-common/permit-specific-conditions process-target-id])
     (rf/subscribe [::entities/entity process-target-id])])
  (fn [[conditions process-target] _]
    (remove (partial condition-model/condition-ineligible-for-approval? process-target) conditions)))

(rf/reg-sub ::new-circulation-data :-> ::new-circulation-data)

(rf/reg-sub ::new-targets
  :<- [::new-circulation-data]
  :-> (comp seq :targets))

(rf/reg-sub ::new-target-group-id
  :<- [::new-circulation-data]
  :-> :target-group-id)

(rf/reg-sub ::conditions
  :<- [::new-circulation-data]
  :-> :conditions)

(rf/reg-event-db ::select-conditions
  (fn [db [_ conditions]]
    (assoc-in db [::new-circulation-data :conditions] conditions)))

(rf/reg-event-db ::update-conditions
  (fn [db [_ fn condition-id]]
    (update-in db [::new-circulation-data :conditions] fn condition-id)))

(rf/reg-sub ::new-target-user-ids
  :<- [::new-circulation-data]
  :-> :target-user-ids)

(rf/reg-sub ::has-at-least-two-target-users?
  :<- [::new-target-user-ids]
  :<- [::new-target-group-id]
  :<- [::new-targets]
  (fn [[target-user-ids target-group-id targets] _]
    (or (du/at-least-two? (set (concat (mapcat :target-user-ids targets) target-user-ids)))
        (du/at-least-two? (set (conj (map :target-group-id targets) target-group-id))))))

(rf/reg-sub ::new-due-date
  :<- [::new-circulation-data]
  :-> :due-date)

(rf/reg-sub ::new-visibility-applicants
  :<- [::new-circulation-data]
  :-> :visibility-applicants)

(rf/reg-sub ::new-visibility-stakeholders
  :<- [::new-circulation-data]
  :-> :visibility-stakeholders)

(rf/reg-sub ::new-description
  :<- [::new-circulation-data]
  :-> :description)

(rf/reg-sub ::new-selected-group-ids
  :<- [::new-targets]
  (fn [targets _]
    (->> targets (map :target-group-id) set)))

(rf/reg-sub ::new-not-selected-stakeholder-groups
  (fn [_]
    [(rf/subscribe [::stakeholder-groups])
     (rf/subscribe [::new-selected-group-ids])])
  (fn [[available-groups selected-groups]]
    (->> available-groups
         (remove #(contains? selected-groups (:db/id %))))))

(defn- users->names-string [users]
  (->> users
       (map user-model/full-name)
       (interpose ", ")
       (reduce str)))

(rf/reg-sub ::stakeholder-names-string
  (fn [[_ target-group-id _]]
    (rf/subscribe [::stakeholder-users target-group-id]))
  (fn [all-users [_ _ target-user-ids]]
    (->> all-users
         (filter #(->> % :db/id ((set target-user-ids))))
         users->names-string)))

(rf/reg-sub ::edit-circulation-data :-> ::edit-circulation-data)

(rf/reg-sub ::approvals
  :<- [::entities/context]
  (fn [ctx [_ process-target-id]]
    (entities/find-entities ctx {:entity/type   :entity.type/approval
                                 :entity/parent process-target-id})))

(rf/reg-sub ::has-approvals?
  (fn [[_ process-target-id]]
    (rf/subscribe [::approvals process-target-id]))
  (fn [approvals _]
    (-> approvals seq boolean)))

(rf/reg-sub ::approval-by-id
  (fn [[_ process-target-id]]
    (rf/subscribe [::approvals process-target-id]))
  (fn [approvals [_ _ approval-id]]
    (->> approvals
         (filter #(= (:db/id %) approval-id))
         first)))

(rf/reg-sub ::edit-data-changes-by-id
  :<- [::edit-circulation-data]
  (fn [data [_ approval-id]]
    (get data approval-id)))

(rf/reg-event-db ::edit-editable-circulation-data
  (rf/path [::edit-circulation-data])
  (fn [data [_ id changes]]
    (update data id #(merge % changes))))

(rf/reg-event-db ::reset-editable-circulation-data
  (rf/path [::edit-circulation-data])
  (fn [data [_ id]]
    (update data id (constantly nil))))

(rf/reg-sub ::edited-due-date
  (fn [[_ approval-id]]
    (rf/subscribe [::edit-data-changes-by-id approval-id]))
  :-> :due-date)

(rf/reg-sub ::approval-task
  (fn [[_ process-target-id approval-id]]
    (rf/subscribe [::approval-by-id process-target-id approval-id]))
  (fn [approval _]
    (-> approval
        :task/_reference
        first)))

(rf/reg-sub ::approval-perms
  (fn [[_ process-target-id approval-id]]
    (rf/subscribe [::approval-by-id process-target-id approval-id]))
  (fn [approval _]
    (-> approval
        :entity/required-read-permissions
        set)))

(rf/reg-sub ::approval-visibility-stakeholders?
  (fn [[_ process-target-id approval-id]]
    (rf/subscribe [::approval-perms process-target-id approval-id]))
  (fn [perms _]
    (or (contains? perms :permission.approval/stakeholder-read)
        (empty? perms))))

(rf/reg-sub ::approval-visibility-applicants?
  (fn [[_ process-target-id approval-id]]
    (rf/subscribe [::approval-perms process-target-id approval-id]))
  (fn [perms _]
    (or (contains? perms :permission.approval/applicant-read)
        (empty? perms))))

(rf/reg-sub ::edited-visibility-stakeholders?
  (fn [[_ approval-id]]
    (rf/subscribe [::edit-data-changes-by-id approval-id]))
  :-> :visibility-stakeholders)

(rf/reg-sub ::edited-visibility-applicants?
  (fn [[_ approval-id]]
    (rf/subscribe [::edit-data-changes-by-id approval-id]))
  :-> :visibility-applicants)

(rf/reg-sub ::edited-stakeholders-visibility?
  (fn [[_ process-target-id approval-id]]
    [(rf/subscribe [::approval-visibility-stakeholders? process-target-id approval-id])
     (rf/subscribe [::edited-visibility-stakeholders? approval-id])])
  (fn [[original-visibility? edited-visibility?] _]
    (if (nil? edited-visibility?)
      original-visibility?
      edited-visibility?)))

(rf/reg-sub ::edited-applicants-visibility?
  (fn [[_ process-target-id approval-id]]
    [(rf/subscribe [::approval-visibility-applicants? process-target-id approval-id])
     (rf/subscribe [::edited-visibility-applicants? approval-id])])
  (fn [[original-visibility? edited-visibility?] _]
    (if (nil? edited-visibility?)
      original-visibility?
      edited-visibility?)))

(rf/reg-sub ::original-edit-data
  (fn [[_ process-target-id approval-id]]
    (rf/subscribe [::approval-by-id process-target-id approval-id]))
  (fn [approval _]
    (let [task        (-> approval :task/_reference first)
          perms       (set (:entity/required-read-permissions approval))
          group-id    (-> task :task/user-group :db/id)
          assignee-id (-> task :task/assignees first :db/id)
          conditions  (approval-model/approval-conditions approval)]
      (-> {:target-group-id         group-id
           :due-date                (-> task :task/due)
           :description             (or (-> task :task/description) "")
           :visibility-stakeholders (or (contains? perms :permission.approval/stakeholder-read)
                                        (empty? perms))
           :visibility-applicants   (or (contains? perms :permission.approval/applicant-read)
                                        (empty? perms))
           :approval-id             (:db/id approval)
           :conditions              conditions}
          (p/assoc-if :target-user-id (when (not= assignee-id group-id)
                                        assignee-id))))))

(rf/reg-sub ::edit-data-with-changes
  (fn [[_ process-target-id approval-id]]
    [(rf/subscribe [::original-edit-data process-target-id approval-id])
     (rf/subscribe [::edit-data-changes-by-id approval-id])])
  (fn [[original-data changes] _]
    (merge (into {} original-data)
           changes)))

(rf/reg-sub ::saving-edit-data-by-id?
  (fn [[_ approval-id]]
    (rf/subscribe [::edit-data-changes-by-id approval-id]))
  (fn [changes _]
    (:saving? changes false)))

(rf/reg-event-db ::saving-edit-data
  (rf/path [::edit-circulation-data])
  (fn [data [_ id]]
    (update data id #(assoc % :saving? true))))

(defn- form-header-box-row [text]
  [:div.bx--row
   [:div.bx--col-xs-12
    [:h3.text-base.font-semibold.uppercase.mb-0
     text]]])

(defn- form-info-box-row [text]
  [:div.bx--row
   [:div.mt-4.bx--col-xs-12
    [:p.p-0.leading-tight text]]])

(defn- date-validation-result [date]
  {:due-date (when-not (str/blank? date)
               (date-util/end-of-day date))})

(defn- due-date-picker [{:keys [tr value callback]}]
  [:div.mt-4.bx--col-xs-12.bx--col-md-6.bx--col-xl-5.bx--col-xxl-4
   [ui/date-picker {:label-text  (tr [:approval/due-date])
                    :value       value
                    :class-input "bg-white"
                    :min-date    (.format date-util/calendar-date-format (date-util/today-end-of-day))
                    :callback    callback}]])

(defn- add-due-date-picker [tr]
  [due-date-picker
   (util/mmap {:value    (<sub [::new-due-date])
               :callback #(>evt [::save-new-circulation-data (date-validation-result %)])}
              tr)])

(defn- edit-due-date-picker [tr process-target-id approval-id]
  (let [edited-date   (<sub [::edited-due-date approval-id])
        original-date (-> (<sub [::approval-task process-target-id approval-id])
                          :task/due)
        value         (or edited-date
                          original-date)
        callback      #(>evt [::edit-editable-circulation-data approval-id (date-validation-result %)])]
    [due-date-picker (util/mmap value callback tr)]))

(defn visibility-edit [{:keys [approval-id tr stakeholders-checked? applicants-checked? stakeholders-on-change applicants-on-change]}]
  [:div.flex.items-center
   (cc/checkbox {:id         (str approval-id "-approval-visibility-all")
                 :label-text (tr [:approval/visibility-stakeholders])
                 :checked    stakeholders-checked?
                 :on-change  stakeholders-on-change})
   (cc/checkbox {:id         (str approval-id "-approval-visibility-applicants")
                 :label-text (tr [:approval/visibility-applicants])
                 :checked    applicants-checked?
                 :on-change  applicants-on-change})])

(defn- visibility-options [{:keys [approval-id tr stakeholders-checked? applicants-checked? stakeholders-on-change applicants-on-change]}]
  [:div.mt-4.bx--col-xs-12.bx--col-md-6.bx--col-xl-5.bx--col-xxl-4
   [:div.bx--label (tr [:approval/additional-visibility])]
   [:div.flex.items-center
    (cc/checkbox {:id         (str approval-id "-approval-visibility-all")
                  :label-text (tr [:approval/visibility-stakeholders])
                  :checked    stakeholders-checked?
                  :on-change  stakeholders-on-change})
    (cc/checkbox {:id         (str approval-id "-approval-visibility-applicants")
                  :label-text (tr [:approval/visibility-applicants])
                  :checked    applicants-checked?
                  :on-change  applicants-on-change})]])

(defn- add-visibility-options [tr]
  [visibility-options
   (util/mmap {:stakeholders-checked?  (<sub [::new-visibility-stakeholders])
               :applicants-checked?    (<sub [::new-visibility-applicants])
               :stakeholders-on-change #(>evt [::save-new-circulation-data {:visibility-stakeholders %}])
               :applicants-on-change   #(>evt [::save-new-circulation-data {:visibility-applicants %}])
               :approval-id            "new-approval"}
              tr)])

(defn- edit-visibility-options [tr process-target-id approval-id]
  [visibility-options
   (util/mmap {:stakeholders-checked?  (<sub [::edited-stakeholders-visibility? process-target-id approval-id])
               :applicants-checked?    (<sub [::edited-applicants-visibility? process-target-id approval-id])
               :stakeholders-on-change #(>evt [::edit-editable-circulation-data approval-id {:visibility-stakeholders %}])
               :applicants-on-change   #(>evt [::edit-editable-circulation-data approval-id {:visibility-applicants %}])}
              tr
              approval-id)])

(defn- description-editor-box-row [{:keys [tr process-target] :as editor-data}]
  (let [ag-id (-> process-target
                  :entity/parent
                  :domain/authority-group
                  :db/id)]
    [:div.bx--row
     [:div.mt-4.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
      [ui-rt/editor-legacy
       (merge {:authority-group-id ag-id
               :label-text         (str (tr [:approval/description]) " (" (tr [:optional]) ")")}
              editor-data)]]]))

(defn- new-description-editor-box-row [tr process-target]
  [description-editor-box-row
   (util/mmap {:id            "new-approval-description"
               :initial-value (<sub [::new-description])
               :on-change     #(>evt [::save-new-circulation-data {:description %}])}
              tr
              process-target)])

(defn- edit-description-editor-box-row [tr process-target approval-id]
  [description-editor-box-row
   (util/mmap {:id            (str approval-id "-approval-description")
               :initial-value (-> (<sub [::approval-task (:db/id process-target) approval-id])
                                  :task/description)}
              tr
              process-target)])

(defn- target-group-pills-box-row [tr]
  (let [targets (<sub [::new-targets])
        groups  (<sub [::stakeholder-groups])]
    [:div.bx--row
     [:div.bx--col-xs-12.flex.flex-wrap
      (futil/for-frag [[idx {:keys [target-group-id target-user-ids]}] (map-indexed vector targets)
                       :let [group        (p/find-first groups {:db/id target-group-id})
                             users?       (seq target-user-ids)
                             names-string (<sub [::stakeholder-names-string target-group-id target-user-ids])]]
        [:div
         {:class (str "cloudpermit--tag cloudpermit--tag--round bg-ui-black text-ui-white justify-between "
                      "mt-2 break-words max-w-full items-center px-2 py-1")
          :style {:height "unset"}}
         [:span
          (->> [(:group/long-name group)
                (when users?
                  names-string)]
               (remove nil?)
               (str/join " - "))]
         [:button.bg-transparent.border-0 {:type :button}
          (cc/icon {:icon        i/remove
                    :description (tr [:remove])
                    :fill        "white"
                    :on-click    #(>evt
                                    [::save-new-circulation-data
                                     {:targets (let [[before after] (split-at idx targets)]
                                                 (vec (concat before (rest after))))}])})]])]]))

(defn- header-box-row [text]
  [:div.bx--row.mt-4>div.bx--col-xs-12>h4.text-sm.font-semibold.leading-snug text])

(defn- select-multiple-users [stakeholder-users target-user-ids tr inputs]
  (let [selected-users (->> stakeholder-users
                            (filter #(->> % :db/id ((set target-user-ids))))
                            set)
        displayed-text (if (seq selected-users)
                         (users->names-string selected-users)
                         (tr [:approval/target-person-any-ph]))]
    [ui-ms/multi-select (assoc inputs
                               :value selected-users
                               :displayed-text displayed-text)]))

(defn- select-single-user [stakeholder-users target-user-id inputs]
  (let [selected-user (p/find-first stakeholder-users {:db/id target-user-id})]
    [ui-select/select (assoc inputs :value selected-user)]))

(defn- select-stakeholder-user [{:keys [id multi? on-change target-group-id target-user-id target-user-ids tr]}]
  (let [stakeholder-users (<sub [::stakeholder-users target-group-id])
        inputs            (util/mmap {:class      "full-width-select"
                                      :data-cy    "select-target-person"
                                      :label-text (tr [:approval/target-person])
                                      :light      true
                                      :val->str   user-model/full-name
                                      :values     stakeholder-users}
                                     id
                                     on-change)]
    [:div.flex.items-end.mt-4.bx--col-xs-12.bx--col-md-4.bx--col-xl-4.bx--col-xxl-3
     (if multi?
       [select-multiple-users stakeholder-users target-user-ids tr inputs]
       [select-single-user stakeholder-users target-user-id inputs])]))

(defn- select-stakeholder-group [{:keys [disabled? id on-change target-group-id tr stakeholder-groups]}]
  [:div.flex.items-end.mt-4.bx--col-xs-12.bx--col-md-4.bx--col-lg-5.bx--col-xl-4..bx--col-xxl-3
   [ui-select/select
    (util/mmap {:allow-empty? false
                :class        "full-width-select"
                :disabled     (or disabled? false)
                :label-text   (tr [:approval/target-group])
                :light        true
                :required     true
                :val->str     :group/long-name
                :value        (p/find-first stakeholder-groups {:db/id target-group-id})
                :values       stakeholder-groups}
               id
               on-change)]])

(defn- add-target-button [target-group-id target-user-ids tr]
  (let [targets (<sub [::new-targets])]
    [:div.flex.items-end.mt-4.mx-4
     (cc/button {:kind     :tertiary
                 :size     :field
                 :icon     i/plus
                 :on-click #(>evt [::save-new-circulation-data (-> {:targets targets}
                                                                   (assoc :target-group-id nil
                                                                          :target-user-ids [])
                                                                   (update :targets
                                                                           p/conjv
                                                                           (merge {:target-group-id target-group-id}
                                                                                  (when (seq target-user-ids)
                                                                                    {:target-user-ids target-user-ids}))))])}
                (tr [:approval/add-new]))]))

(rf/reg-event-db ::save-new-target-group-id
  (rf/path [::new-circulation-data])
  (fn [data [_ target-id]]
    (assoc data
           :target-group-id target-id
           :target-user-ids [])))

(defn- new-target-selection-box-row [tr]
  (let [target-group-id      (<sub [::new-target-group-id])
        target-user-ids      (<sub [::new-target-user-ids])
        target-user-id       (first target-user-ids)
        enable-multi-select? (empty? (<sub [::conditions]))]
    (if (seq (<sub [::new-not-selected-stakeholder-groups]))
      [:div.bx--row
       [select-stakeholder-group
        (util/mmap {:id                 "new-select-circulation-group"
                    :on-change          #(>evt [::save-new-target-group-id (:db/id %)])
                    :stakeholder-groups (<sub [::new-not-selected-stakeholder-groups])}
                   target-group-id
                   tr)]
       (when target-group-id
         [:<>
          [select-stakeholder-user
           (merge {:id        (str "select-target-user-for-group-" target-group-id)
                   :multi?    enable-multi-select?
                   :on-change (fn [users]
                                (>evt [::save-new-circulation-data
                                       {:target-user-ids (if enable-multi-select?
                                                           (keep :db/id users)
                                                           [(:db/id users)])}]))}
                  (if enable-multi-select?
                    (util/mmap target-user-ids target-group-id tr)
                    (util/mmap target-user-id target-group-id tr)))]
          [add-target-button target-group-id target-user-ids tr]])]
      (tr [:approval/available-approver-groups-chosen]))))

(defn- edit-target-selection-box-row [tr approval-id target-group-id target-user-id]
  [:div.bx--row
   [select-stakeholder-group
    (util/mmap {:id                 (str approval-id "-edit-select-circulation-group")
                :disabled?          true
                :stakeholder-groups (<sub [::stakeholder-groups])}
               target-group-id tr)]
   [select-stakeholder-user
    (util/mmap {:id        (str approval-id "-edit-select-circulation-user")
                :multi?    false
                :on-change #(>evt [::edit-editable-circulation-data
                                   approval-id
                                   {:target-user-id (:db/id %)}])}
               target-group-id
               target-user-id
               tr)]])

(defn- new-button-section [tr process-target]
  (let [data (<sub [::new-circulation-data])]
    [:div.mt-10.flex.flex-row.justify-between
     [:div.flex.ml-auto
      [ui-action-buttons/save-cancel-buttons
       (util/mmap {:on-cancel      (fn [_]
                                     (>evt [::close-new-circulation-form])
                                     (>evt [::reset-new-circulation-data]))
                   :on-save        (fn []
                                     (>evt [:app.front.process/signal
                                            process-target
                                            [:process.signal/create-approval (cond-> (dissoc data :target-group-id :target-user-ids)
                                                                               (-> data :description str/blank?) (dissoc :description)
                                                                               (-> data :due-date nil?)          (dissoc :due-date))]])
                                     (>evt [::close-new-circulation-form])
                                     (>evt [::reset-new-circulation-data]))
                   :save-disabled? (empty? (<sub [::new-targets]))
                   :save-tr        :process.signal/create-approval}
                  tr)]]]))

(defn- edit-button-section [tr process-target-id approval-id user-invalid? finish-evt]
  (let [original-data     (<sub [::original-edit-data process-target-id approval-id])
        data-with-changes (<sub [::edit-data-with-changes process-target-id approval-id])
        no-data-changes?  (= original-data data-with-changes)
        saving-changes?   (<sub [::saving-edit-data-by-id? approval-id])]
    [:div.mt-10.flex.flex-row.justify-between
     (when finish-evt
       (cc/button {:kind       :tertiary
                   :class-name "mt-2"
                   :on-click   #(>evt finish-evt)}
                  (tr [:approval/finish])))
     [ui-action-buttons/save-cancel-buttons
      (util/mmap {:disabled? (or no-data-changes?
                                 saving-changes?)
                  :on-cancel #(>evt [::reset-editable-circulation-data approval-id])
                  :on-save   (fn []
                               (>evt [::saving-edit-data approval-id])
                               (>evt [::process/signal
                                      process-target-id
                                      [:process.signal/edit-approval-request (process-data-for-edit user-invalid? data-with-changes)]
                                      {:on-success [::reset-editable-circulation-data approval-id]}]))
                  :save-tr   :process.signal/edit-approval-request}
                 tr)]]))

(defn render-approval-conditions-form [{:keys [tr editing? selected-conditions displayed-conditions
                                               set-conditions-fn change-condition-fn]}]
  [:div
   (when (and (not editing?)
              (seq displayed-conditions))
     (let [disabled? (<sub [::has-at-least-two-target-users?])]
       [:div.bx--row
        [:div.mt-4.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
         (cc/button (cond-> {:kind          :tertiary
                             :size          :small
                             :icon          (if selected-conditions i/remove i/plus)
                             :disabled      disabled?
                             :icon-position :right
                             :class-name    "mt-2"
                             :on-click      set-conditions-fn}
                      disabled?
                      (assoc :title (tr [:approval/condition-has-multiple-approver])))
                    (tr (if selected-conditions
                          [:approval/remove-conditions]
                          [:approval/include-conditions])))]]))

   (when selected-conditions
     [:div
      [:div.bx--row
       [:div.mt-4.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
        [:span (tr [:approval/condition-info])]]]
      [:div.bx--row
       [:div.mt-2.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
        #_{:clj-kondo/ignore [:deprecated-var]}
        [ui-table/responsive-table {:tr                 tr
                                    :class              "mt-4"
                                    :expandable?        false
                                    :headerless?        true
                                    :row-class-override "items-center p-4 bg-white font-bold"}
         [{:flex 1}
          {:flex 12}]
         (map (fn [{id :db/id :as condition}]
                (let [checked? (contains? selected-conditions id)]
                  {:key  id
                   :opts {:class ["mb-2 border-solid border-l-4 rounded-md" (if checked? "border-ui-violet" "border-ui-gray")]}
                   :cols [(when (not editing?)
                            {:content (cc/checkbox {:id         (str "select-condition" id)
                                                    :label-text ""
                                                    :checked    (contains? selected-conditions id)
                                                    :on-change  #(change-condition-fn checked? id)})})
                          {:content [:span (str (-> condition :ordered/order inc)
                                                ". "
                                                (:application.condition/title condition))]}]}))
              displayed-conditions)]]]])])

(defn approval-conditions [{:keys [tr process-target]} *data editing?]
  (let [selected-conditions  (get @*data :conditions #{})
        selected-conditions? (seq selected-conditions)
        displayed-conditions (if editing?
                               (when selected-conditions?
                                 (map :approval-condition/condition selected-conditions))
                               (<sub [::selectable-conditions (:db/id process-target)]))]
    [render-approval-conditions-form
     (util/mmap
       {:set-conditions-fn   #(swap! *data assoc :conditions
                                     (if selected-conditions?
                                       #{}
                                       (->> displayed-conditions (map :db/id) set)))
        :change-condition-fn (fn [checked? id]
                               (swap! *data update :conditions (fnil (if checked? disj conj) #{}) id))}
       tr editing? selected-conditions? selected-conditions displayed-conditions)]))

(defn approval-conditions-v2 [{:keys [tr process-target conditions]} editing?]
  (let [selected-conditions   (some-> (if editing?
                                        (map :db/id conditions)
                                        (<sub [::conditions]))
                                      seq
                                      set)
        displayed-conditions  (if editing?
                                (->> (map :approval-condition/condition conditions)
                                     (sort-by :ordered/order))
                                (<sub [::selectable-conditions (:db/id process-target)]))
        selected-target-users (<sub [::new-target-user-ids])]
    [render-approval-conditions-form
     (util/mmap
       {:set-conditions-fn   #(let [condition-ids (if selected-conditions
                                                    #{}
                                                    (set (map :db/id displayed-conditions)))]
                                (when (seq condition-ids)
                                  (>evt [::save-new-circulation-data {:target-user-ids [(first selected-target-users)]}]))
                                (>evt [::select-conditions condition-ids]))
        :change-condition-fn (fn [checked? id]
                               (>evt [::update-conditions (fnil (if checked? disj conj) #{}) id]))}
       tr editing? selected-conditions displayed-conditions)]))

(defn ws-config-change-indicator [{:keys [tr attr approval-id class]}]
  (when-let [approval-configs (<sub [::deviant-approval-ws-configs approval-id attr])]
    (let [lang (<sub [:language])]
      [:div.text-xs.italic {:class (or class "bx--col-xs-12 mt-2")}
       (->> (config-model/config-path-as-string lang approval-configs)
            vector
            (tr [attr]))])))

(defn approval-form [{:keys [tr process-target] :as ctx} approval-id *data save-signal & {:keys [finish-evt]}]
  (r/with-let [original-data @*data]
    (let [groups             (<sub [::stakeholder-groups])
          selected-group-ids (->> @*data :targets (map :target-group-id) set)
          group-id           (-> @*data :target-group-id)
          user-id            (-> @*data :target-user-id)
          editing-request?   (= save-signal :process.signal/edit-approval-request)
          valid?             (or editing-request?
                                 (-> @*data :targets seq))
          default-groups?    (and (not editing-request?)
                                  (-> @*data :targets seq))
          user-invalid?      (<sub [::no-such-user? group-id user-id])
          on-save            (fn []
                               (>evt [:app.front.process/signal
                                      process-target
                                      [save-signal (if editing-request?
                                                     (process-data-for-edit user-invalid? @*data)
                                                     (process-data-for-create @*data))]])
                               (>evt [::close-new-circulation-form]))
          authority-group-id (-> process-target
                                 :entity/parent
                                 :domain/authority-group
                                 :db/id)]
      (if (seq groups)
        [:form.new-approval-form.bg-ui-blue-10.rounded.border-ui-violet-100
         {:on-submit (fn [e] (.preventDefault e) (on-save))}
         [:div.bx--row
          [:div.bx--col-xs-12
           [:h3.text-base.font-semibold.uppercase.mb-0
            (tr [(if editing-request?
                   :approval/edit-request-heading
                   :approval/new-request-heading)])]]]
         [:div.bx--row
          [:div.mt-4.bx--col-xs-12
           [:p.p-0.leading-tight (tr [:approval/new-request-info])]]]
         [:div.bx--row
          [:div.mt-4.bx--col-xs-12.bx--col-md-6.bx--col-xl-5.bx--col-xxl-4
           [ui/date-picker {:label-text  (tr [:approval/due-date])
                            :value       (-> @*data :due-date)
                            :class-input "bg-white"
                            :min-date    (.format date-util/calendar-date-format (date-util/today-end-of-day))
                            :callback    #(swap! *data assoc :due-date (when-not (str/blank? %)
                                                                         (date-util/end-of-day %)))}]]
          [:div.mt-4.bx--col-xs-12.bx--col-md-6.bx--col-xl-5.bx--col-xxl-4
           [:div.bx--label (tr [:approval/additional-visibility])]
           [visibility-edit (util/mmap {:stakeholders-checked?  (-> @*data :visibility-stakeholders)
                                        :applicants-checked?    (-> @*data :visibility-applicants)
                                        :stakeholders-on-change #(swap! *data assoc :visibility-stakeholders %)
                                        :applicants-on-change   #(swap! *data assoc :visibility-applicants %)}
                                       tr approval-id)]]]
         [:div.bx--row
          [:div.mt-4.bx--col-xs-12.bx--col-xl-10.bx--col-xxl-8
           [ui-rt/editor-legacy {:id                 (str approval-id "-description")
                                 :label-text         (str (tr [:approval/description]) " (" (tr [:optional]) ")")
                                 :initial-value      (:description @*data)
                                 :on-change          (when (not editing-request?) #(swap! *data assoc :description %))
                                 :authority-group-id authority-group-id
                                 :tr                 tr}]]
          (when editing-request?
            [ws-config-change-indicator (util/mmap {:attr :approval/creation-group-configs}
                                                   tr approval-id)])]

         [ui-sep/horizontal]

         [approval-conditions ctx *data editing-request?]

         (when (seq (:targets @*data))
           [:<>
            [:div.bx--row.mt-4
             [:div.bx--col-xs-12
              [:h4.text-sm.font-semibold.leading-snug (tr [:approval/send-request-to])]]]
            [:div.bx--row
             [:div.bx--col-xs-12.flex.flex-wrap
              (futil/for-frag [[idx {:keys [target-group-id target-user-id]}] (map-indexed vector (:targets @*data))
                               :let [group (p/find-first groups {:db/id target-group-id})
                                     user  (when target-user-id
                                             (p/find-first (<sub [::stakeholder-users target-group-id]) {:db/id target-user-id}))]]
                [:div
                 {:class (str "cloudpermit--tag cloudpermit--tag--round bg-ui-black text-ui-white justify-between "
                              "mt-2 break-words max-w-full items-center px-2 py-1")
                  :style {:height "unset"}}
                 [:span
                  (->> [(:group/long-name group)
                        (when user
                          (user-model/full-name user))]
                       (remove nil?)
                       (str/join " - "))]
                 [:button.bg-transparent.border-0 {:type :button}
                  (cc/icon {:icon        i/remove
                            :description (tr [:remove])
                            :fill        "white"
                            :on-click    #(swap! *data update :targets (fn [targets]
                                                                         (let [[before after] (split-at idx targets)]
                                                                           (vec (concat before (rest after))))))})]])]]])
         [:div.bx--row.mt-4
          [:div.bx--col-xs-12
           [:h4.text-sm.font-semibold.leading-snug (tr [:approval/add-new-stakeholder])]]]
         [:div.bx--row
          [:div.flex.items-end.mt-4.bx--col-xs-12.bx--col-md-4.bx--col-lg-5.bx--col-xl-4..bx--col-xxl-3
           [ui-select/select
            {:id           (str approval-id "-approval-group")
             :class        "full-width-select"
             :light        true
             :label-text   (tr [:approval/target-group])
             :disabled     editing-request?
             :required     true
             :value        (p/find-first groups {:db/id group-id})
             :allow-empty? false
             :on-change    #(swap! *data assoc
                                   :target-group-id (:db/id %)
                                   :target-user-id nil)
             :values       (->> groups
                                (filter #(not (contains? selected-group-ids (:db/id %)))))
             :val->str     :group/long-name}]]
          [:div.flex.items-end.mt-4.bx--col-xs-12.bx--col-md-4.bx--col-xl-4.bx--col-xxl-3
           [ui-select/select
            {:id         (str approval-id "-approval-person")
             :data-cy    "approval-person"
             :class      "full-width-select"
             :light      true
             :label-text (tr [:approval/target-person])
             :values     (<sub [::stakeholder-users group-id])
             :value      (<sub [::stakeholder-user group-id user-id])
             :on-change  (fn [user]
                           (swap! *data assoc :target-user-id (:db/id user)))
             :val->str   user-model/full-name}]]
          (when-not editing-request?
            [:div.flex.items-end.mt-4.bx--col-xs-12.bx--col-md-4.bx--col-lg-3.bx--col-xl-2
             (cc/button {:kind     :tertiary
                         :size     :field
                         :icon     i/plus
                         :disabled (or (nil? group-id)
                                       (let [targets (:targets @*data)]
                                         (-> (p/find-first targets (p/assoc-if {:target-group-id group-id}
                                                                               :target-user-id user-id))
                                             some?)))
                         :on-click #(swap! *data (fn [{:keys [target-user-id target-group-id] :as data}]
                                                   (-> (assoc data
                                                              :target-group-id nil
                                                              :target-user-id nil)
                                                       (update :targets
                                                               p/conjv
                                                               (merge {:target-group-id target-group-id}
                                                                      (when (int? target-user-id)
                                                                        {:target-user-id target-user-id}))))))}
                        (tr [:approval/add-new]))])]
         (when user-invalid?
           [ui/help (tr [:approvals/no-such-user])])

         [ui-sep/horizontal]

         [:div.mt-10.flex.flex-row.justify-between
          (when finish-evt
            (cc/button {:kind       :tertiary
                        :class-name "mt-2"
                        :on-click   #(>evt finish-evt)}
                       (tr [:approval/finish])))

          [:div.flex {:class (when-not finish-evt "ml-auto")}
           (cc/button {:kind       :tertiary
                       :class-name "mt-2 mr-2"
                       :disabled   (and (= @*data original-data)
                                        editing-request?)
                       :on-click   (fn [_]
                                     (reset! *data original-data)
                                     (when-not editing-request?
                                       (>evt [::close-new-circulation-form])))}
                      (tr [:cancel]))
           (cc/button {:kind       :primary
                       :class-name "mt-2"
                       :disabled   (or (not valid?)
                                       (and (not default-groups?)
                                            (= @*data original-data)))
                       :on-click   on-save}
                      (tr [save-signal]))]]]
        [:div.bold (tr [:approval/no-target-groups])]))))

(defn- edit-approval-form-v2 [approval {:keys [finish-evt process-target]}]
  (let [approval-id       (:db/id approval)
        process-target-id (:db/id process-target)
        tr                (<sub [::ws-subs/tr])
        groups            (<sub [::stakeholder-groups])
        data              (<sub [::edit-data-with-changes process-target-id approval-id])
        target-group-id   (-> data :target-group-id)
        target-user-id    (-> data :target-user-id)
        user-invalid?     (<sub [::no-such-user? target-group-id target-user-id])
        conditions        (approval-model/approval-conditions approval)]
    (if (seq groups)
      [:div.new-approval-form.bg-ui-blue-10.rounded.border-ui-violet-100
       [form-header-box-row (tr [:approval/edit-request-heading])]
       [form-info-box-row (tr [:approval/new-request-info])]
       [:div.bx--row
        [edit-due-date-picker tr process-target-id approval-id]
        [edit-visibility-options tr process-target-id approval-id]]
       [edit-description-editor-box-row tr process-target approval-id]
       [ws-config-change-indicator (util/mmap {:attr :approval/creation-group-configs} tr approval-id)]

       [ui-sep/horizontal]

       [approval-conditions-v2 (util/mmap data tr process-target conditions) true]
       [header-box-row (tr [:approval/add-new-stakeholder])]
       [edit-target-selection-box-row tr approval-id target-group-id target-user-id]
       (when user-invalid?
         [ui/help (tr [:approvals/no-such-user])])

       [ui-sep/horizontal]

       [edit-button-section tr process-target-id approval-id user-invalid? finish-evt]]
      [:div.bold (tr [:approval/no-target-groups])])))

(defn edit-approval-form [ctx approval opts]
  (if (ff/enabled? :feature-flag.id/multi-circulation-requests)
    [edit-approval-form-v2 approval opts]
    (r/with-let [task        (-> approval :task/_reference first)
                 perms       (set (:entity/required-read-permissions approval))
                 group-id    (-> task :task/user-group :db/id)
                 assignee-id (-> task :task/assignees first :db/id)
                 conditions  (->> approval
                                  :entity/_parent
                                  (map (comp :db/id :approval-condition/condition))
                                  set)
                 *data       (r/atom (-> {:target-group-id         group-id
                                          :due-date                (-> task :task/due)
                                          :description             (or (-> task :task/description) "")
                                          :visibility-stakeholders (or (contains? perms :permission.approval/stakeholder-read)
                                                                       (empty? perms))
                                          :visibility-applicants   (or (contains? perms :permission.approval/applicant-read)
                                                                       (empty? perms))
                                          :approval-id             (:db/id approval)
                                          :conditions              conditions}
                                         (p/assoc-if :target-user-id (when (not= assignee-id group-id)
                                                                       assignee-id))))]
      [approval-form ctx (:db/id approval) *data :process.signal/edit-approval-request opts])))

(defn- new-approval-form-v2 [{:keys [tr process-target] :as ctx}]
  (if (<sub [::stakeholder-groups])
    [:div.new-approval-form.bg-ui-blue-10.rounded.border-ui-violet-100
     [form-header-box-row
      (tr [:approval/new-request-heading])]
     [form-info-box-row
      (tr [:approval/new-request-info])]
     [:div.bx--row
      [add-due-date-picker tr]
      [add-visibility-options tr]]
     [new-description-editor-box-row tr process-target]

     [ui-sep/horizontal]

     [approval-conditions-v2 ctx false]
     (when (<sub [::new-targets])
       [:<>
        [header-box-row (tr [:approval/send-request-to])]
        [target-group-pills-box-row tr]])

     (when-not
       (and
         (seq (<sub [::conditions]))
         (<sub [::new-targets]))
       [:<>
        [header-box-row (tr [:approval/add-new-stakeholder])]
        [new-target-selection-box-row tr]])

     [new-button-section tr process-target]]
    [:div.bold (tr [:approval/no-target-groups])]))

(defn new-approval-form [{:keys [default-approver-groups] :as ctx}]
  (if (ff/enabled? :feature-flag.id/multi-circulation-requests)
    [new-approval-form-v2 ctx]
    (r/with-let [*data (r/atom (p/assoc-if {:target-group-id         nil
                                            :target-user-id          nil
                                            :due-date                nil
                                            :description             ""
                                            :visibility-stakeholders false
                                            :visibility-applicants   false}
                                           :targets (->> default-approver-groups
                                                         (map #(hash-map :target-group-id %))
                                                         seq)))]
      [approval-form ctx "new-approval" *data :process.signal/create-approval])))
